/**
 * Liveness test for the MJPEG <img>.
 *
 * Why polling rather than an event: when a multipart/x-mixed-replace stream
 * dies mid-flight the browser silently discards the decoded picture —
 * naturalWidth drops to 0, `complete` stays true, the element stays mounted,
 * and *no* event fires. Not `error`, not `abort`, not `load`. Verified in
 * Chrome by restarting the backend under a live feed. So an onError handler
 * alone can never notice; that is why the panel used to stay black until the
 * operator reloaded the page.
 *
 * A request that fails outright (an expired feed token 401s) does fire `error`,
 * so the <img> keeps its onError too. This covers the other half.
 */

/** How long a fresh <img> may show nothing before we call it dead. Must clear
 *  the backend's own connect grace (stream_state.CONNECT_GRACE_SECONDS = 10 s),
 *  since during that window the server is still sending a real CONNECTING
 *  picture and the feed is perfectly healthy. */
export const FEED_FIRST_FRAME_TIMEOUT_MS = 15_000;

export interface FeedHealth {
  /** 0 once the browser has thrown the picture away. */
  naturalWidth: number;
  /** Has this <img> instance ever decoded a frame? */
  everHadPicture: boolean;
  /** Age of this <img> instance. */
  msSinceMount: number;
  firstFrameTimeoutMs?: number;
}

export function isFeedDead({
  naturalWidth,
  everHadPicture,
  msSinceMount,
  firstFrameTimeoutMs = FEED_FIRST_FRAME_TIMEOUT_MS,
}: FeedHealth): boolean {
  if (naturalWidth > 0) return false;          // there is a picture on screen
  if (everHadPicture) return true;             // it had one and lost it
  return msSinceMount >= firstFrameTimeoutMs;  // never arrived at all
}
